import { supabase } from './supabase';

export type KleedBehandelingBron = 'Office-App' | 'Phone-Agent' | 'Brein' | 'Kantoor-Handmatig';

export interface BehandelingCatalogItem {
  sku: string;
  name: string;
  description?: string;
  unitPriceInclVat?: number;
  alreadyLinked: boolean;
}

export interface BehandelingCatalog {
  success: boolean;
  message?: string;
  errorCode?: string;
  parentName?: string;
  parentTotaalInclVat?: number;
  items: BehandelingCatalogItem[];
}

export interface BehandelingResult {
  success: boolean;
  message?: string;
  errorCode?: string;
  childId?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

export async function fetchBehandelingCatalog(
  parentLeadProductId: string
): Promise<BehandelingCatalog> {
  const headers = await authHeaders();
  const qp = new URLSearchParams({ parentLeadProductId });
  const response = await fetch(`/api/offertes/kleed-behandeling?${qp.toString()}`, { headers });
  const data = await response.json().catch(() => ({}));
  // Een dode read mag nooit als lege lijst lezen (11-09: lege dialog op een
  // 500 met alleen `error`/`detail`): altijd een boodschap bij falen.
  const message =
    data.message ||
    data.detail ||
    data.error ||
    (data.success !== true ? `Catalogus laden mislukt (${response.status})` : undefined);
  return {
    success: data.success === true,
    message,
    errorCode: data.errorCode,
    parentName: data.parentName,
    parentTotaalInclVat: data.parentTotaalInclVat,
    items: Array.isArray(data.items) ? (data.items as BehandelingCatalogItem[]) : [],
  };
}

export interface AddBehandelingArgs {
  parentLeadProductId: string;
  behandelingCode: string;
  bron?: KleedBehandelingBron;
}

export async function addKleedBehandeling(args: AddBehandelingArgs): Promise<BehandelingResult> {
  const headers = await authHeaders();
  const body = {
    parentLeadProductId: args.parentLeadProductId,
    behandelingCode: args.behandelingCode,
    bron: args.bron || 'Office-App',
  };
  const response = await fetch('/api/offertes/kleed-behandeling', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({} as BehandelingResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Behandeling toevoegen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return data as BehandelingResult;
}

export async function removeKleedBehandeling(childId: string): Promise<BehandelingResult> {
  const headers = await authHeaders();
  const qp = new URLSearchParams({ childId });
  const response = await fetch(`/api/offertes/kleed-behandeling?${qp.toString()}`, {
    method: 'DELETE',
    headers,
  });
  const data = await response.json().catch(() => ({} as BehandelingResult));
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      message: data.message || `Behandeling verwijderen mislukt (${response.status})`,
      errorCode: data.errorCode,
    };
  }
  return data as BehandelingResult;
}
